import { nanoid } from "nanoid";
import { z } from "zod";
import {
  nanoidSchema,
  scalarSet,
  slugSchema,
  timestampSchema,
  unixTimestamp
} from "../utility/index.js";
import { defaultWebsite, websiteSchema } from "../project/website.js";
import { activeDeploymentSchema } from "../project/active-deployment.js";
import { defaultVersion, versionSchema } from "../project/version/version.js";
const logoSchema = z.object({
  darkMode: z.string(),
  lightMode: z.string()
});
const projectSchema = z.object({
  /** Unique identifier for the project */
  uid: nanoidSchema,
  /** Name of the project */
  name: z.string(),
  /** Versions of the project */
  versions: z.record(z.string(), versionSchema),
  /** Active template id for the project */
  activeTemplateId: z.string().optional().default(""),
  /** Active theme id for the project */
  activeThemeId: z.string().optional().default(""),
  /** Active version id for the project */
  activeVersionId: z.string().optional().default(""),
  /** Timestamp when the project was created */
  createdAt: timestampSchema,
  /** Timestamp when the project was last updated */
  updatedAt: timestampSchema,
  /** Logo for the project */
  logo: logoSchema,
  /** Deployed site configuration for a project */
  website: websiteSchema.describe(
    "Deployed site configuration for a project"
  ),
  /** Typesense id for the project */
  typesenseId: z.number().optional(),
  /** Record of the last successful deployment */
  activeDeployment: activeDeploymentSchema.nullable().optional(),
  /** Track last published timestamp to allow build interrupts */
  lastPublished: timestampSchema.nullable().default(null),
  /** Uid of the most recent publish record (failed or successful) */
  lastPublishedUid: z.string().nullable().optional(),
  /** Uid of the login portal customization object for private projects */
  loginPortalUid: z.string().optional(),
  /** Private projects require a login from a user in an access group */
  isPrivate: z.boolean().default(false),
  /** Set of allowed access groups */
  accessGroups: scalarSet(z.set(nanoidSchema).max(50)).default([])
}).transform((project) => {
  if (Object.keys(project.versions).length === 0) {
    const v = defaultVersion({});
    project.versions[v.uid] = v;
  }
  if (!project.activeVersionId) {
    project.activeVersionId = Object.keys(project.versions)[0];
  }
  return project;
});
function defaultProject(initialState = {}) {
  const v = defaultVersion({});
  return projectSchema.parse({
    uid: nanoid(),
    name: "",
    activeTemplateId: "",
    activeThemeId: "",
    activeVersionId: v.uid,
    createdAt: unixTimestamp(),
    updatedAt: unixTimestamp(),
    header: [],
    logo: {
      darkMode: "",
      lightMode: ""
    },
    versions: {
      [v.uid]: v
    },
    website: defaultWebsite(),
    activeDeployment: null,
    lastPublishedUid: null,
    loginPortalUid: "",
    ...initialState
  });
}
const githubProjectSchema = z.object({
  uid: nanoidSchema,
  name: z.string(),
  slug: slugSchema.optional(),
  createdAt: timestampSchema,
  /** Last publish status info */
  publishStatus: z.string().default(""),
  /** Last publish output message */
  publishMessage: z.string().default(""),
  /** Is the site private? */
  isPrivate: z.boolean().default(false),
  /** Set of allowed access groups */
  accessGroups: scalarSet(z.set(nanoidSchema).max(50)).default([]),
  /**
   *
   * Reference info for the link repository
   *
   * Optional for projects which are not linked
   */
  repository: z.object({
    linkedBy: z.string(),
    id: z.number(),
    name: z.string().min(2),
    configPath: z.string().default(""),
    branch: z.string().default(""),
    publishOnMerge: z.boolean().default(false)
  }).nullable().optional(),
  activeDeployment: activeDeploymentSchema.nullable().optional(),
  lastPublishedUid: z.string().nullable().optional(),
  /** Track last published timestamp to allow build interrupts */
  lastPublished: timestampSchema.nullable().default(null),
  typesenseId: z.number().optional(),
  loginPortalUid: z.string().optional().default("")
});
function isGithubProject(project) {
  return "publishStatus" in project;
}
export {
  defaultProject,
  githubProjectSchema,
  isGithubProject,
  logoSchema,
  projectSchema
};
//# sourceMappingURL=project.js.map
